import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
} from '@wyz/ui';
import {
  UseFormGetFieldState,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import {
  CheckboxField,
  CriteriaBuilder,
  MatchedCustomersTable,
  NumberField,
  SelectField,
  TextField,
} from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { SeasonSelector } from '~/purchase/components';
import { settingTemplateFormSchema } from '~/connect/schemas';
import { ISettingTemplateForm, ISettingTemplatePayload } from '~/connect/types';

import { TYRE_IDENTIFIER_FIELD, ValidationErrorResponse } from '@wyz/types';
import { Link } from 'react-router';
import { useManageCriteria } from '~/common/api';
import { FEATURES } from '~/common/helpers';
import { useAccessControl, useValidationForm } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { TYRE_IDENTIFIER_FIELD_LABEL } from '~/common/translators';
import {
  CommonErrorCodesEnumType,
  ITranslatedVehicleTypeGroup,
} from '~/common/types';
import { useManageParameters } from '~/connect/api';
import { SettingTemplateHelpers } from '~/connect/helpers';
import { VehicleTypeGroupField } from '~/connect/components';
import { useManageParameters as useManageCommonParameters } from '~/common/api';

type Props =
  | {
      defaultValues?: Partial<ISettingTemplateForm>;
      mode?: 'CREATE' | 'UPDATE';
      isLoading: boolean;
      onSubmit: (
        data: ISettingTemplatePayload,
        formState: {
          criteria: ReturnType<UseFormGetFieldState<ISettingTemplateForm>>;
        },
      ) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
      coreVehicleTypeGroups: ITranslatedVehicleTypeGroup[];
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<ISettingTemplateForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
      coreVehicleTypeGroups: ITranslatedVehicleTypeGroup[];
    };

export function SettingTemplateForm({
  mode = 'CREATE',
  defaultValues = SettingTemplateHelpers.SETTING_TEMPLATE_FORM_DEFAULT_VALUES,
  isLoading,
  onSubmit,
  errors,
  coreVehicleTypeGroups,
}: Props) {
  const { t } = useI18n();
  const acl = useAccessControl();

  const methods = useForm<ISettingTemplateForm>({
    defaultValues,
    resolver: zodResolver(settingTemplateFormSchema),
  });
  const watchedJ0 = Boolean(
    useWatch({
      name: 'alternativeOfferSetting.j0',
      control: methods.control,
    }),
  );

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });

  const { getReferenceGroups, getPriceFrameTypesQuery } = useManageParameters({
    getReferenceGroups: {
      enabled: true,
    },
    getPriceFrameTypesQuery: {
      enabled: true,
    },
  });
  const { getSeasons } = useManageCommonParameters({
    getSeasons: { enabled: true },
  });

  const { field: defaultSeasonsField } = useController({
    name: 'generalSetting.defaultSeasons',
    control: methods.control,
  });

  const watchedReferenceGroupPublicKey = useWatch({
    name: 'generalSetting.referenceGroupPublicKey',
    control: methods.control,
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => {
        onSubmit?.(
          SettingTemplateHelpers.fromFormToPayload(
            data,
            manageCriteria.getAll.data?.data ?? [],
          ),
          { criteria: methods.getFieldState('criteria') },
        );
      }}
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs='3'>
              <TextField
                required
                name='name'
                label={<Trans i18nKey='app.common.model_name' />}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Flex justifyContent='between'>
            <Title heading='lg' color='tertiary' weight='bold'>
              <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
              <span className='ms-2'>
                <Trans i18nKey='app.common.criteria' />
              </span>
            </Title>
          </Flex>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder name={'criteria'}>
            <Row className='mt-4'>
              <Col md={6}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.SETTING_TEMPLATE}
                  name={'criteria'}
                />
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='settings' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.setting_template.form.settings.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3} sm={12}>
              <SelectField
                name='generalSetting.referenceGroupPublicKey'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.manufacturer_reference' />
                }
                options={
                  getReferenceGroups.data?.data.map((referenceGroup) => ({
                    value: referenceGroup.publicKey,
                    label: referenceGroup.name,
                  })) ?? []
                }
                onSelect={(value) => {
                  if (!value) {
                    methods.setValue(
                      'generalSetting.alternativeReferenceEnabled',
                      false,
                    );
                  }
                }}
                isClearable
                isLoading={!getReferenceGroups.isSettled}
              />
            </Col>
            <Col md={3} sm={12}>
              <SelectField
                name='generalSetting.alternativeSupplierPriceFrameType'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.price_frame_types' />
                }
                options={getPriceFrameTypesQuery.data ?? []}
                isClearable
                isLoading={!getPriceFrameTypesQuery.isSettled}
              />
            </Col>
            <Col md={3} sm={12}>
              <SelectField
                name='generalSetting.wholesalerSupplierPriceFrameType'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.wholesaler_price_frame_types' />
                }
                options={getPriceFrameTypesQuery.data ?? []}
                isLoading={!getPriceFrameTypesQuery.isSettled}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col md={4} sm={12}>
              <CheckboxField
                id='generalSetting.alternativeReferenceEnabled'
                name='generalSetting.alternativeReferenceEnabled'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.alternative_reference_enabled' />
                }
                disabled={!watchedReferenceGroupPublicKey}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3} sm={12}>
              <CheckboxField
                id='generalSetting.backOrder'
                name='generalSetting.backOrder'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.back_order' />
                }
              />
            </Col>
            <Col md={3} sm={12}>
              <CheckboxField
                id='alternativeOfferSetting.bestPrice'
                name='alternativeOfferSetting.bestPrice'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.best_price' />
                }
              />
            </Col>
            <Col md={3} sm={12}>
              <CheckboxField
                id='alternativeOfferSetting.bestDelay'
                name='alternativeOfferSetting.bestDelay'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.best_delivery' />
                }
              />
            </Col>
            <Col md={3} sm={12}>
              <SelectField<true>
                name='generalSetting.tyreIdentifierFields'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.displayable_identifier' />
                }
                options={Object.values(TYRE_IDENTIFIER_FIELD).map(
                  (identifier) => ({
                    value: identifier,
                    label: t(TYRE_IDENTIFIER_FIELD_LABEL[identifier]),
                  }),
                )}
                isClearable
                isLoading={!getReferenceGroups.isSettled}
                isMulti={true}
                maxShown={Infinity}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3} sm={12}>
              <CheckboxField
                id='alternativeOfferSetting.j0'
                name='alternativeOfferSetting.j0'
                label={
                  <Trans i18nKey='app.setting_template.form.settings.j0' />
                }
              />
            </Col>
            {watchedJ0 && (
              <>
                <Col md={3} sm={12}>
                  <CheckboxField
                    id='alternativeOfferSetting.j0Today'
                    name='alternativeOfferSetting.j0Today'
                    label={
                      <Trans i18nKey='app.setting_template.form.settings.today' />
                    }
                  />
                </Col>
                <Col md={3} sm={12}>
                  <CheckboxField
                    id='alternativeOfferSetting.j0NextDay'
                    name='alternativeOfferSetting.j0NextDay'
                    label={
                      <Trans i18nKey='app.setting_template.form.settings.next_day' />
                    }
                  />
                </Col>
                <Col md={3} sm={12}>
                  <NumberField
                    name='alternativeOfferSetting.j0MaxQuantity'
                    label={
                      <Trans i18nKey='app.setting_template.form.settings.j0_max_quantity' />
                    }
                    required={watchedJ0}
                    withKnobs={false}
                  />
                </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='settings' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.setting_template.form.search_parameters.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3} sm={12}>
              <Text>
                <Trans i18nKey='app.setting_template.form.display_parameters.research' />
              </Text>
              <CheckboxField
                id='generalSetting.manualSearch'
                name='generalSetting.manualSearch'
                label={
                  <Trans i18nKey='app.setting_template.form.display_parameters.manual_search' />
                }
                asTextField={false}
              />
            </Col>
          </Row>
          <Row className='mt-4'>
            <Col md={6} sm={12}>
              <SeasonSelector
                seasons={defaultSeasonsField.value ?? []}
                availableSeasons={getSeasons.data?.data ?? []}
                isLoading={!getSeasons.isSettled}
                onChange={defaultSeasonsField.onChange}
                label='app.setting_template.form.search_parameters.default_seasons'
                required={false}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='settings' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.setting_template.form.display_parameters.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3} sm={12}>
              <CheckboxField
                id='generalSetting.displayBestPriceTag'
                name='generalSetting.displayBestPriceTag'
                label={
                  <Trans i18nKey='app.setting_template.form.display_parameters.show_best_price_tag' />
                }
              />
            </Col>
            <Col md={3} sm={12}>
              <CheckboxField
                id='generalSetting.displayBestDelayTag'
                name='generalSetting.displayBestDelayTag'
                label={
                  <Trans i18nKey='app.setting_template.form.display_parameters.show_best_delay_tag' />
                }
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='settings' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.sidebar.platform.vehicle_types' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <VehicleTypeGroupField
            coreVehicleTypeGroups={coreVehicleTypeGroups}
          />
        </Card.Body>
      </Card>
      {acl.isEnabledFeatures([
        FEATURES.REDISTRIBUTION,
        FEATURES.DESTOCKING,
      ]) && (
        <Card className='mb-section'>
          <Card.Header className='pb-0'>
            <Title heading='lg' color='tertiary' weight='bold'>
              <Icon icon='settings' width='1.5rem' height='1.5rem' />
              <span className='ms-2'>
                <Trans i18nKey='app.setting_template.form.destocking.title' />
              </span>
            </Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3} sm={12}>
                <CheckboxField
                  id='generalSetting.destockingEnabled'
                  name='generalSetting.destockingEnabled'
                  label={
                    <Trans i18nKey='app.setting_template.form.destocking.enable_for_model' />
                  }
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      {mode !== 'READ_ONLY' && (
        <Card className='mb-section'>
          <Card.Body>
            <Flex className='space-x-4' justifyContent='between'>
              <Button
                id='cancel'
                as={Link}
                variant='secondary'
                type='button'
                to='/platform/distribution/setting-templates'
              >
                <Trans i18nKey='app.common.button.cancel' />
              </Button>
              <Button
                id='submit'
                variant='primary'
                type='submit'
                disabled={isLoading}
              >
                <Trans
                  i18nKey={
                    mode === 'CREATE'
                      ? 'app.setting_template.form.buttons.create'
                      : 'app.setting_template.form.buttons.update'
                  }
                />
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
}
